'use client';

import './styles/default.scss';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useCallback, useState } from 'react';
import { COOKIES } from '@/utils/cookies';
import { getCookieValue } from '@/utils/cookies/client';
import { usePlaytechEnrollment } from '@/hooks/use-playtech-enrollment';
import { PlaytechGameLauncher } from './playtech-game-launcher';
import { fetchPlaytechGameConfig } from '@/logic/playtech-enrollment/fetch-playtech-game-config';
import { PLAYTECH_FALLBACK_GAME } from '@/logic/playtech-enrollment/find-playtech-game';
import type { PlaytechGameConfig } from '@/logic/playtech-enrollment/find-playtech-game';
import { ddRumAction, ddRumError } from '@/utils/datadog';
import { ddActionPokerGateShown, ddActionPokerGateCtaClicked } from '@/utils/datadog/poker';
import { gtmPush } from '@/utils/tracking/gtm';
import { GTM } from '@/utils/tracking/gtm/constants';

export type PlaytechGateProps = {
    children: ReactNode;
    fallback?: ReactNode;
    notEnrolledMessage?: ReactNode;
    notEnrolledTitle?: ReactNode;
    ctaLabel?: ReactElement;
};

const DEFAULT_TITLE = 'Activate your Poker account';
const DEFAULT_BODY =
    'Open a poker game to choose a username and finish setting up your account before claiming promotions.';
const DEFAULT_CTA_LABEL = <span>Open Poker Lobby</span>;

export const PlaytechGate: React.FC<PlaytechGateProps> = ({
    children,
    fallback,
    notEnrolledMessage,
    notEnrolledTitle,
    ctaLabel
}) => {
    const { status } = usePlaytechEnrollment();
    const [gameConfig, setGameConfig] = useState<PlaytechGameConfig | null>(null);

    useEffect(() => {
        if (status !== 'not-enrolled') return;

        let cancelled = false;
        fetchPlaytechGameConfig(false)
            .then((config) => {
                if (!cancelled) {
                    setGameConfig(config);
                    ddRumAction(
                        ddActionPokerGateShown({
                            region: getCookieValue(COOKIES.REGION) ?? '',
                            userId: getCookieValue(COOKIES.USER_ID) ?? ''
                        })
                    );
                }
            })
            .catch((error: unknown) => {
                if (!cancelled) {
                    ddRumError({
                        name: 'POKER_GAME_CONFIG_FETCH_FAILED',
                        description:
                            'Failed to fetch Playtech game config from lobby zones — falling back to default game',
                        use_case: 'GAME_LAUNCHER',
                        stack: error instanceof Error ? error : undefined
                    });
                    setGameConfig(PLAYTECH_FALLBACK_GAME);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [status]);

    const handleGateCtaClicked = useCallback(() => {
        ddRumAction(ddActionPokerGateCtaClicked());
        gtmPush({
            event: GTM.EVENTS.PLAYTECH_SETUP_LAUNCHED,
            gaEventCategory: 'Playtech',
            gaEventAction: 'Game Launched',
            gaEventLabel: gameConfig?.gameName ?? '',
            module: 'Playtech Enrollment'
        });
    }, [gameConfig]);

    const isLoading = status === 'loading' || (status === 'not-enrolled' && gameConfig === null);

    if (isLoading) {
        if (fallback !== undefined) {
            return <>{fallback}</>;
        }
        return (
            <div className="playtech-gate-loading" data-testid="playtech-gate-loading" aria-busy="true">
                <div className="playtech-gate-loading__spinner" />
            </div>
        );
    }

    if (status === 'not-enrolled' && gameConfig !== null) {
        return (
            <div className="playtech-gate" data-testid="playtech-gate" role="status">
                <div className="playtech-gate__overlay">
                    <div className="playtech-gate__card">
                        <div className="playtech-gate__header">
                            <div className="playtech-gate__logo" />
                        </div>
                        <div className="playtech-gate__body">
                            <h2 className="playtech-gate__title">{notEnrolledTitle ?? DEFAULT_TITLE}</h2>
                            <p className="playtech-gate__message">{notEnrolledMessage ?? DEFAULT_BODY}</p>
                        </div>
                        <div className="playtech-gate__footer">
                            <PlaytechGameLauncher
                                module="playtech-enrollment-gate"
                                onGameLaunch={handleGateCtaClicked}
                                gameConfig={gameConfig}>
                                {ctaLabel ?? DEFAULT_CTA_LABEL}
                            </PlaytechGameLauncher>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};
